/**
 * SOLID Review
 * S — Strong. One concern: the client's SESSION as data — who this tab is, where it is in the funnel,
 *     which table it is heading to — and the two records the tab keeps across a reload.
 * O — Good. A new session field is one key in `ClientSession`; its changes schema follows from it.
 * L — N/A — no inheritance hierarchy.
 * I — Good. The snapshot, its changes and the two stored records: what a session decision reads and writes.
 * D — Good. zod and the shared primitives only.
 */
import { z } from 'zod';
import { Identifier } from './primitives';
import { ClientConsent } from './ConsentMoves';
import { ClientAds } from './AdsMoves';
import { ClientMedia } from './MediaMoves';
import { ClientDiagnostics } from './DiagnosticsMoves';
import { ClientDiceRanking } from './DiceRankingMoves';

/**
 * THE SESSION AS THE CLIENT HOLDS IT (frontend strict-ports plan, FE2, 2026-10-03).
 *
 * The table (FE1) says what is happening at the table. The session says who is at it and how they got
 * there: the account this tab plays as, the player it is seated as, the screen it shows, and the table
 * it is on its way to. Every field was a store field written from React — the auth store's identity,
 * screen and pending join, the game store's local user and restoring flag — or local state of a hook.
 *
 * WHAT IS NOT HERE: the JWT. A registered account reaches the Domain as `kind: 'registered'` and its
 * player id, never as the token (the plan's rule: the JWT never crosses a port).
 */

/**
 * Every screen the app routes between. GAME is the table; the rest are the entry funnel and its forms.
 * `REGISTER` went in strict-ports FE3: nothing had routed to it since the entry funnel (`0190fb72`) made
 * the email screen sign up in place, and its screen went with it.
 */
export const ClientScreen = z.enum([
  'WELCOME', 'ENTER_CODE', 'GUEST_CHOICE', 'VERIFY_EMAIL', 'LOGIN', 'VERIFY_LOGIN', 'PAYMENT_SUCCESS', 'GAME',
]).meta({ id: 'ClientScreen' });

/**
 * The account this tab plays as. `registered` holds a JWT (inside Infrastructure); `guest` has a stored
 * guest identity; `anonymous` has neither, and is asked for a name before it can sit down.
 */
const ClientAccount = z.object({
  kind: z.enum(['registered', 'guest', 'anonymous']),
  playerId: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  matchCredits: z.number().int(),
}).meta({ id: 'ClientAccount' });

/** The player this tab is seated as — the game store's `localUser`, as much of it as anything reads. */
const ClientPlayer = z.object({
  playerId: Identifier,
  displayName: z.string(),
  matchCredits: z.number().int(),
  isAdmin: z.boolean(),
}).meta({ id: 'ClientPlayer' });

/** The game this tab is in, kept across a reload or an OS tab discard (`meyer_resume`). */
export const ClientResume = z.object({
  playerId: Identifier,
  roomId: Identifier,
  roomCode: z.string().min(1),
}).meta({ id: 'ClientResume' });

/** A guest identity, kept across a reload so a returning guest is the same player. */
const ClientGuest = z.object({ playerId: Identifier, displayName: z.string() }).meta({ id: 'ClientGuest' });

export const ClientSession = z.object({
  account: ClientAccount,
  /** Null until identity is known: the player's own `me`, the stored guest, or a name just chosen. */
  player: ClientPlayer.nullable(),
  screen: ClientScreen,
  /** A table code the player chose to join, waiting for identity before the join runs. */
  pendingRoomCode: z.string().nullable(),
  /** The funnel ended without a code: host a new table once identity is known. */
  hostOnEntry: z.boolean(),
  /** Why the last join did not happen — shown on the welcome screen. */
  pendingJoinError: z.string().nullable(),
  /** The address a sign-in or sign-up code was sent to, between the email screen and the code screen (FE3). */
  pendingEmail: z.string().nullable(),
  /** The browser's cookie consent, as the CMP last reported it (FE4b). */
  consent: ClientConsent,
  /** The ad provider the server chose for the seated player, and for whom it was asked (FE4d). */
  ads: ClientAds,
  /** The table's video chat — opt-in, the idle rule's state, presence reported (FE5c). */
  media: ClientMedia,
  /** What this page load's error reporting has sent — the budget and the per-error window (client error reporting). */
  diagnostics: ClientDiagnostics,
  /**
   * The rules the table is played by: Meyer's combinations as the server ranks them (`diceRanking`), lowest first —
   * empty until they have arrived (frontend-domain-oo P2). The session's, not the table's: a table comes and goes,
   * the rules stay for the page. Every table decision is handed them beside the table.
   */
  diceRanking: ClientDiceRanking,
  /** "Restoring your game…": a stored game is being checked or rejoined. */
  restoring: z.boolean(),
  /** An entry into the game screen is in flight; a second one is refused (StrictMode runs effects twice). */
  entering: z.boolean(),
  /** The auto-host is creating a table, and why it failed if it did. */
  hosting: z.boolean(),
  hostError: z.string().nullable(),
}).meta({ id: 'ClientSession' });

/** What a decision changes in the session: any field, each replaced whole. */
export const ClientSessionChanges = ClientSession.partial().meta({ id: 'ClientSessionChanges' });

/**
 * What the tab keeps after a decision, written through `ClientTabStorage` in the same step as the store
 * (FE2: "resume persistence is done in the shell's apply step, not by a reactive effect"). `resume: null`
 * forgets the stored game; absent leaves it as it is.
 */
export const ClientPersist = z.object({
  resume: ClientResume.nullable().optional(),
  /** A guest identity to keep, or null to forget the stored one (a sign-in or a sign-out, FE3). */
  guest: ClientGuest.nullable().optional(),
}).meta({ id: 'ClientPersist' });

/** What the tab holds when a session starts or a game screen is entered. */
export const ClientStored = z.object({
  guest: ClientGuest.nullable(),
  resume: ClientResume.nullable(),
}).meta({ id: 'ClientStored' });

export type IStateClientSession = z.infer<typeof ClientSession>;
export type IPropsClientSessionChanges = z.infer<typeof ClientSessionChanges>;
export type IEntityClientPlayer = z.infer<typeof ClientPlayer>;
export type IEntityClientAccount = z.infer<typeof ClientAccount>;
export type IEntityClientResume = z.infer<typeof ClientResume>;
export type IEntityClientGuest = z.infer<typeof ClientGuest>;
export type IEntityClientStored = z.infer<typeof ClientStored>;
export type IPropsClientPersist = z.infer<typeof ClientPersist>;
export type TClientScreen = z.infer<typeof ClientScreen>;
